import type { ButtonProps } from "@mui/material/Button";
import MuiButton from "@mui/material/Button";
import theme, { createStyles, sx, type SxStyles, ThemeColors } from "core/utilities/theme";
import type React from "react";
import { type CSSProperties, type RefObject } from "react";

export const classes = createStyles({
  ml: {
    marginLeft: theme.spacing(1),
  },
  mr: {
    marginRight: theme.spacing(1),
  },
  root: {
    "& svg": {
      marginRight: "3px",
    },
  },
});

export type ButtonActions = "primary" | "secondary" | "tertiary" | "warning" | "deleteWarning";

export interface ComponentProps {
  disabled?: boolean;
  small?: boolean;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  e2eId?: string;
  action: ButtonActions;
  mr?: boolean;
  ml?: boolean;
  fullWidth?: boolean;
  className?: SxStyles;
  submit?: boolean;
  title?: string;
  buttonRef?: RefObject<HTMLButtonElement>;
  inheritColor?: boolean; // use this when the button is not on white background color
  buttonIcon?: boolean;
  buttonIconSelected?: boolean;
  ariaHaspopup?: boolean;
  ariaExpanded?: boolean;
  loading?: boolean;
}

type ActionToVariant = Record<ButtonActions, "text" | "outlined" | "contained">;

export const buttonActionToVariant: ActionToVariant = {
  primary: "contained",
  secondary: "outlined",
  tertiary: "text",
  warning: "contained",
  deleteWarning: "outlined",
};

type ActionToColor = Record<ButtonActions, "primary" | "secondary" | "inherit">;

export const buttonActionToColor: ActionToColor = {
  primary: "secondary",
  secondary: "primary",
  tertiary: "primary",
  warning: "primary",
  deleteWarning: "primary",
};

function Button({
  children,
  disabled,
  onClick,
  e2eId,
  action,
  ml,
  mr,
  fullWidth,
  className,
  small,
  submit,
  title,
  buttonRef,
  inheritColor,
  buttonIcon,
  buttonIconSelected,
  ariaHaspopup,
  ariaExpanded,
  loading,
}: ComponentProps) {
  // Warning: Style logic to be kept in sync with ButtonLink component

  const style: CSSProperties = { minWidth: "auto" };
  if (buttonIcon) {
    style.minWidth = 0;
    if (action !== "tertiary") style.padding = "5px 9px 4px 9px";
    if (buttonIconSelected) style.color = theme.palette.secondary.main;
  }

  // CSS trick to show the 'title' tooltip even though the button is disabled
  if (disabled) style.pointerEvents = "auto";

  if (action === "secondary" && !inheritColor) style.backgroundColor = "white";

  if (disabled !== true && action === "warning") {
    style.color = "white";
    style.backgroundColor = ThemeColors.warning;
  }

  if (disabled !== true && action === "deleteWarning") {
    style.color = ThemeColors.deleteWarning;
    style.borderColor = ThemeColors.deleteWarning;
  }

  if (fullWidth) {
    style.minWidth = undefined;
  }

  const buttonProps: ButtonProps = {};

  if (ariaHaspopup && !disabled) {
    buttonProps["aria-haspopup"] = true;
  }

  if (ariaExpanded !== undefined) {
    buttonProps["aria-expanded"] = ariaExpanded;
  }

  return (
    <MuiButton
      {...buttonProps}
      ref={buttonRef}
      size={small ? "small" : undefined}
      variant={buttonActionToVariant[action]}
      color={inheritColor ? "inherit" : buttonActionToColor[action]}
      sx={sx(classes.root, ml ? classes.ml : undefined, mr ? classes.mr : undefined, className)}
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      data-e2e-button={e2eId || ""}
      type={submit ? "submit" : "button"}
      title={title}
      style={style}
      data-e2e-button-loading={loading}
    >
      {children}
    </MuiButton>
  );
}

export default Button;
